import OpenAI from 'openai';
import { markdownToHTML } from '../utils/markdownUtils';
import { normalizeListSpacing } from '../utils/listNormalizer';
import { WhyYoullLoveTemplate } from '../components/templates/WhyYoullLoveTemplate';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

/**
 * Enhanced error handler for OpenAI API calls
 * Provides clear, actionable error messages for common failure scenarios
 */
function handleOpenAIError(error, context = 'OpenAI API request') {
  console.error(`Error in ${context}:`, error);
  
  const errorMessage = error?.message || '';
  const errorStatus = error?.status || error?.response?.status;
  const errorType = error?.constructor?.name || '';
  const errorCause = error?.cause?.message || '';
  const errorStack = error?.stack || '';
  
  // CRITICAL: Detect generic "Connection error" from OpenAI SDK (happens BEFORE network call completes)
  // This is the most common error and indicates network-level blocking
  const isGenericConnectionError = errorMessage === 'Connection error.' || errorMessage === 'Connection error';
  
  // CRITICAL: Enhanced detection for browser extension interference
  // Check if error stack contains chrome-extension:// patterns
  const isBrowserExtensionError = errorStack?.includes('chrome-extension://') || 
                                   errorStack?.includes('moz-extension://') ||
                                   errorStack?.includes('safari-extension://');
  
  // NEW: Extract extension ID from error stack for precise identification
  const extensionIdMatch = errorStack?.match(/chrome-extension:\/\/([a-z]+)/);
  const extensionId = extensionIdMatch?.[1];
  
  // CRITICAL: Enhanced detection for "Failed to fetch" - browser/network level error
  // This includes checking the error cause chain and stack trace for underlying fetch failures
  const isFetchError = errorMessage?.includes('Failed to fetch') || 
                       errorType === 'TypeError'|| errorCause?.includes('Failed to fetch') ||
                       errorMessage?.includes('fetch failed') ||
                       errorCause?.includes('fetch failed') ||
                       errorStack?.includes('Failed to fetch');
  
  // CRITICAL: Check for generic "Connection error" which often wraps "Failed to fetch"
  const isConnectionError = errorMessage?.includes('Connection error');
  
  // NEW: Priority handling for generic "Connection error" from OpenAI SDK
  // This happens when the request is blocked BEFORE it even reaches the network layer
  if (isGenericConnectionError) {
    console.warn(`🚨 OpenAI Connection Blocked - Immediate Action Required

⚠️ ERROR TYPE: Generic "Connection error" from OpenAI SDK
This means the request was blocked BEFORE it could reach OpenAI servers.

🎯 PRIMARY CAUSE (95% probability): Browser Extension Blocking
Browser extensions (especially ad blockers and privacy tools) are intercepting and blocking OpenAI API requests.

✅ IMMEDIATE FIX - Try These Steps IN ORDER:

STEP 1: Quick Test (30 seconds)
1. Open Incognito/Private window: Ctrl+Shift+N (Chrome/Edge) or Ctrl+Shift+P (Firefox)
2. Visit your app in incognito mode (extensions are disabled by default)
3. If it works → Browser extension is definitely the problem
4. If it still fails → Go to STEP 2

STEP 2: Disable All Extensions (1 minute)
1. Go to: chrome://extensions (or edge://extensions, about:addons for Firefox)
2. Click "Remove" or toggle OFF for ALL extensions temporarily
3. Refresh your app and test again
4. If it works → Re-enable extensions ONE BY ONE to find the culprit

STEP 3: Check These Common Blockers
Look for and disable these extensions specifically:
• uBlock Origin (most common blocker)
• AdBlock / AdBlock Plus / AdGuard
• Privacy Badger / Ghostery
• HTTPS Everywhere / NoScript
• Brave Shields (if using Brave browser)
• Any VPN or Proxy extensions

STEP 4: Whitelist OpenAI Domains
If you want to keep your extensions:
1. Click the extension icon in your browser toolbar
2. Find "Settings" or "Whitelist" options
3. Add these domains to allow list:
   • api.openai.com
   • *.openai.com
4. Save settings and refresh

🔍 OTHER POSSIBLE CAUSES (Less likely):
• Corporate/School Network: Firewall blocking api.openai.com
• VPN/Proxy: Interfering with HTTPS requests
• Antivirus: Blocking outbound API calls
• ISP/Router: Blocking specific domains

💡 VERIFY YOUR SETUP:
1. API Key Valid? Check .env file: VITE_OPENAI_API_KEY
2. Internet Working? Try visiting https://api.openai.com in new tab
3. Using VPN? Try disabling it temporarily
4. Corporate Network? Check with IT department

🆘 STILL NOT WORKING?
1. Try a completely different browser (Chrome → Firefox or vice versa)
2. Test on a different network (mobile hotspot, different WiFi)
3. Check if api.openai.com is accessible: ping api.openai.com
4. Clear browser cache and cookies, then restart browser

📝 TECHNICAL DETAILS:
Error Location: ${errorStack?.split('\n')?.[1]?.trim() || 'OpenAI SDK makeRequest'}
Error Type: ${errorType}
Browser: ${navigator?.userAgent || 'Unknown'}
`);
    // Don't throw - let the calling function handle gracefully with fallback
    return;
  }
  
  // If we detect browser extension interference specifically, provide targeted guidance
  if (isBrowserExtensionError) {
    const extensionInfo = extensionId 
      ? `\n\n🎯 IDENTIFIED BLOCKING EXTENSION:\nExtension ID: ${extensionId}\nLocation: chrome-extension://${extensionId}/`
      : '';
    
    console.warn(`🚨 CONFIRMED: Browser Extension Blocking OpenAI API

${extensionInfo}

✅ IMMEDIATE FIX (Choose ONE method):

METHOD 1: Disable Specific Extension (Recommended)
1. Copy this extension ID: ${extensionId || '[see console for full path]'}
2. Go to chrome://extensions
3. Find the extension with this ID
4. Click "Remove" or toggle it OFF
5. Refresh this page and try again

METHOD 2: Whitelist OpenAI Domain
1. Click the extension icon in your browser toolbar
2. Look for "Settings" or "Whitelist" options
3. Add these domains to allowed list:
   • api.openai.com
   • *.openai.com
4. Save and refresh this page

METHOD 3: Temporary Disable All Extensions (Testing)
1. Open Incognito/Private window (extensions disabled by default)
2. Or go to chrome://extensions and disable all
3. Test if it works without extensions
4. Re-enable extensions one by one to find the culprit

🔍 COMMON CULPRITS:
• uBlock Origin
• AdBlock / AdBlock Plus
• Privacy Badger
• Ghostery
• Any VPN extensions
`);
    // Don't throw - let the calling function handle gracefully with fallback
    return;
  }
  
  // If we detect fetch/connection errors, provide comprehensive troubleshooting
  if (isFetchError || isConnectionError) {
    console.warn(`⚠️ Unable to Connect to OpenAI API

🚨 MOST COMMON CAUSE (90% of cases):
Browser extensions (ad blockers, privacy tools) are blocking the request.

📋 QUICK FIX STEPS:
1. Open Browser Console (F12) → Look for "chrome-extension://" errors
2. If you see chrome-extension:// → A browser extension is blocking the API
3. Temporarily disable ALL extensions: chrome://extensions
4. Refresh page and try again
5. If it works → Re-enable extensions one by one to find the culprit

💡 OTHER POSSIBLE CAUSES:
• Network/Internet connectivity issues (check wifi/ethernet)
• Corporate firewall blocking api.openai.com
• VPN interfering with HTTPS requests
• Antivirus software blocking connections
• Router/ISP-level blocking

🔑 VERIFY THESE:
✓ Can you visit https://api.openai.com in a new tab? (should show "404" or OpenAI response)
✓ Is your OpenAI API key valid? Check .env file: VITE_OPENAI_API_KEY
✓ Are you using a VPN? Try disabling it temporarily
✓ Is your internet connection stable? Try other websites

🆘 ADVANCED DEBUGGING:
• Test in Incognito mode (extensions disabled): Ctrl+Shift+N
• Try a different browser (Chrome, Firefox, Edge)
• Check browser console for detailed error messages
• Temporarily disable antivirus/firewall to test`);
    // Don't throw - let the calling function handle gracefully with fallback
    return;
  }
  
  // Check for specific OpenAI API error types
  if (errorMessage?.includes('API key') || errorStatus === 401) {
    throw new Error('OpenAI API key is invalid or missing. Please check your VITE_OPENAI_API_KEY environment variable in the .env file.');
  } else if (errorMessage?.includes('quota') || errorMessage?.includes('insufficient_quota') || errorStatus === 429) {
    throw new Error('OpenAI API quota exceeded. Please check your OpenAI account billing and usage limits at https://platform.openai.com/account/billing');
  } else if (errorMessage?.includes('rate limit') || errorMessage?.includes('rate_limit_exceeded')) {
    throw new Error('OpenAI API rate limit reached. Please wait a few minutes and try again, or upgrade your OpenAI plan for higher limits.');
  } else if (errorStatus === 500 || errorStatus === 502 || errorStatus === 503) {
    throw new Error('OpenAI service is temporarily unavailable. Please try again in a few minutes.');
  } else if (errorMessage?.includes('timeout')) {
    throw new Error('Request timed out. The OpenAI API is taking too long to respond. Please try again.');
  }
  
  // Generic fallback with original error for debugging
  throw new Error(`${context} failed: ${errorMessage || 'Unknown error occurred. Please check your network connection and API key configuration.'}`);
}

/**
 * Validate OpenAI API key before making requests
 */
function validateAPIKey() {
  if (!import.meta.env?.VITE_OPENAI_API_KEY || import.meta.env?.VITE_OPENAI_API_KEY === 'your-openai-api-key-here') {
    throw new Error('OpenAI API key is not configured. Please add your VITE_OPENAI_API_KEY to the .env file.\n\nSteps:\n1. Create/edit .env file in project root\n2. Add: VITE_OPENAI_API_KEY=your-actual-api-key\n3. Restart the development server\n4. Get your API key from: https://platform.openai.com/api-keys');
  }
}

/**
 * Generate blog article content using GPT-4 with custom prompt template
 */
export async function generateBlogArticle(promptTemplate, focusKeyword, title) {
  try {
    validateAPIKey();
    
    const systemPrompt = `You are an expert content writer. Generate comprehensive, SEO-optimized blog articles that are engaging, informative, and valuable for readers.

CRITICAL OUTPUT FORMAT REQUIREMENT:
- Output ONLY clean HTML
- NO Markdown syntax (**, ##, -, *, etc.)
- Use proper HTML tags: <h2>, <h3>, <p>, <strong>, <em>, <ul>, <li>, etc.
- All content must be WordPress-ready HTML
- LISTS: Format as consecutive <li> items with NO blank lines between them
- DO NOT wrap <li> items in <p> tags
- DO NOT insert <br> tags around list items`;
    
    // Replace placeholders in prompt template
    const processedPrompt = promptTemplate
      ?.replace(/\{focusKeyword\}/g, focusKeyword)
      ?.replace(/\{title\}/g, title) || 
      `Create a detailed 2000-word article for: "${title || focusKeyword}"
      
Focus Keyword: "${focusKeyword}"

Generate comprehensive, SEO-optimized content with proper HTML formatting.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: processedPrompt },
      ],
      temperature: 0.4,
      max_tokens: 2000,
    });

    let content = response?.choices?.[0]?.message?.content;
    
    // CRITICAL: Convert any remaining Markdown to HTML as fallback
    content = markdownToHTML(content);
    
    // NEW: Normalize list spacing to remove blank lines between list items
    content = normalizeListSpacing(content);
    
    return content;
  } catch (error) {
    handleOpenAIError(error, 'Generate blog article');
  }
}

/**
 * Generate recipe article content using GPT-4
 */
export async function generateRecipeArticle({
  focusKeyword,
  shortDescription,
  ingredients,
  sitemapsUrls,
  promptTemplate,
}) {
  try {
    validateAPIKey();
    
    const systemPrompt = promptTemplate || `You are an expert recipe and food content writer. Generate comprehensive, SEO-optimized recipe articles that are engaging, informative, and valuable for readers.

CRITICAL OUTPUT FORMAT REQUIREMENT:
- Output ONLY clean HTML
- NO Markdown syntax (**, ##, -, *, etc.)
- Use proper HTML tags: <h2>, <h3>, <p>, <strong>, <em>, <ul>, <li>, etc.
- All content must be WordPress-ready HTML
- LISTS: Format as consecutive <li> items with NO blank lines between them
- DO NOT wrap <li> items in <p> tags
- DO NOT insert <br> tags around list items`;
    
    const userPrompt = `
Create a detailed 2000-word recipe article for: "${focusKeyword}"

${shortDescription ? `Context: ${shortDescription}` : ''}
${ingredients ? `Ingredients to include: ${ingredients}` : ''}
${sitemapsUrls ? `Use these URLs for internal/external links in the Dynamic Related Recipes section: ${sitemapsUrls}` : ''}

Structure the article in this EXACT order:
1. Introduction (engaging hook, 150-200 words)
2. Why You'll Love This Recipe (3-5 compelling bullet points highlighting benefits, ease, taste, flexibility) - THIS SECTION IS ALREADY PROVIDED AS A TEMPLATE, YOU MUST GENERATE THE BULLET POINTS
3. Ingredients (organized list with measurements)
4. Step-by-Step Instructions (detailed, numbered steps)
5. Expert Tips for Success (practical advice)
6. Variations and Substitutions (creative alternatives)
7. Serving Suggestions (presentation ideas)
8. FAQs (5-7 common questions with answers)
9. Final Thoughts (conclusion with call-to-action)
10. Dynamic Related Recipes (3-5 related recipe links using provided URLs)

CRITICAL FORMATTING REQUIREMENTS:
- Use ONLY HTML tags: <h2>, <h3>, <p>, <strong>, <em>, <ul>, <ol>, <li>, etc.
- NO Markdown syntax whatsoever (**, ##, -, *, etc.)
- Focus keyword "${focusKeyword}" naturally integrated throughout
- Use subheadings (H2, H3) for structure
- Write in an engaging, conversational tone
- Include specific measurements and times
- Make it actionable and easy to follow
- Optimize for SEO without keyword stuffing
- Output must be WordPress-ready clean HTML

CRITICAL LIST FORMATTING:
- Format bullet lists as consecutive <li> items with NO blank lines between them
- DO NOT wrap <li> items inside <p> tags
- DO NOT insert <br> tags around list items
- Each <li> should be on a single line immediately after the previous </li>

IMPORTANT: For "Why You'll Love This Recipe" section, ONLY provide the bullet point content (3-5 items). The template styling will be applied automatically.
`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      temperature: 0.4,
      max_tokens: 2000,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'recipe_article_with_benefits',
          schema: {
            type: 'object',
            properties: {
              introduction: { type: 'string' },
              whyYoullLoveBenefits: { 
                type: 'array', 
                items: { type: 'string' },
                description: '3-5 compelling reasons why readers will love this recipe'
              },
              remainingContent: { type: 'string' }
            },
            required: ['introduction', 'whyYoullLoveBenefits', 'remainingContent'],
            additionalProperties: false
          }
        }
      }
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    
    // ✅ Assemble article with template insertion
    let content = result?.introduction || '';
    
    // ✅ Insert "Why You'll Love This Recipe" template with AI-generated benefits
    if (result?.whyYoullLoveBenefits?.length > 0) {
      content += '\n\n' + WhyYoullLoveTemplate({ 
        benefits: result?.whyYoullLoveBenefits 
      });
    }
    
    content += '\n\n' + (result?.remainingContent || '');
    
    // CRITICAL: Convert any remaining Markdown to HTML as fallback
    content = markdownToHTML(content);
    
    // NEW: Normalize list spacing to remove blank lines between list items
    content = normalizeListSpacing(content);
    
    return content;
  } catch (error) {
    handleOpenAIError(error, 'Generate recipe article');
  }
}

/**
 * Generate SEO metadata
 */
export async function generateSEOMetadata(focusKeyword, articleTitle, articleContent) {
  try {
    validateAPIKey();
    
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an SEO expert specializing in RankMath optimization. Generate concise, effective SEO metadata.',
        },
        {
          role: 'user',
          content: `Generate SEO metadata for this recipe article:
Focus Keyword: "${focusKeyword}"
Title: "${articleTitle}"
Content Preview: ${articleContent?.substring(0, 500)}

Provide:
1. SEO Title (50-60 characters, include focus keyword)
2. URL Slug (short, keyword-based, lowercase with hyphens)
3. Meta Description (140-160 characters, compelling, includes focus keyword)
4. Secondary Keywords (5-7 related keywords)
5. Tags (5-8 relevant tags)`,
        },
      ],
      temperature: 0.4,
      max_tokens: 2000,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'seo_metadata',
          schema: {
            type: 'object',
            properties: {
              seo_title: { type: 'string' },
              permalink: { type: 'string' },
              meta_description: { type: 'string' },
              secondary_keywords: { type: 'array', items: { type: 'string' } },
              tags: { type: 'array', items: { type: 'string' } },
            },
            required: ['seo_title', 'permalink', 'meta_description', 'secondary_keywords', 'tags'],
            additionalProperties: false,
          },
        },
      },
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    handleOpenAIError(error, 'Generate SEO metadata');
  }
}

/**
 * Generate image metadata (alt text, title, caption, description)
 */
export async function generateImageMetadata(focusKeyword, imageLocation) {
  try {
    validateAPIKey();
    
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'Generate descriptive, SEO-friendly image metadata for recipe images.',
        },
        {
          role: 'user',
          content: `Generate image metadata for a ${imageLocation} image in a recipe article about "${focusKeyword}"`,
        },
      ],
      temperature: 0.4,
      max_tokens: 2000,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'image_metadata',
          schema: {
            type: 'object',
            properties: {
              alt_text: { type: 'string' },
              title: { type: 'string' },
              caption: { type: 'string' },
              description: { type: 'string' },
            },
            required: ['alt_text', 'title', 'caption', 'description'],
            additionalProperties: false,
          },
        },
      },
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error generating image metadata:', error);
    // Return fallback metadata instead of throwing
    return {
      alt_text: `${focusKeyword} - ${imageLocation}`,
      title: focusKeyword,
      caption: `${focusKeyword} recipe image`,
      description: `Image for ${focusKeyword} recipe`,
    };
  }
}

/**
 * Generate MidJourney prompt
 */
export async function generateMidjourneyPrompt({
  focusKeyword,
  ingredients,
  referenceImageUrl,
  templatePrompt,
  aspectRatio = '3:2',
}) {
  try {
    validateAPIKey();
    
    const systemPrompt = `You are an expert at creating detailed MidJourney prompts for professional food photography. 

CRITICAL OUTPUT REQUIREMENTS:
- Return ONLY the raw prompt text
- NO labels like "MidJourney Prompt:" or "Prompt:"
- NO quotes or markdown formatting (**, **, etc.)
- NO prefixes, suffixes, or explanatory text
- Output must be plain text ready for direct use in MidJourney
- Start directly with descriptive content`;
    
    const userPrompt = `Create a MidJourney prompt for food photography of: "${focusKeyword}"
${ingredients ? `Ingredients: ${ingredients}` : ''}
${referenceImageUrl ? `Reference style from: ${referenceImageUrl}` : ''}

Base template: ${templatePrompt || 'Professional food photography, appetizing presentation, natural lighting, magazine quality'}

Enhance with artistic details, composition suggestions, lighting, styling, and visual elements. Make it specific and compelling.
Include aspect ratio: --ar ${aspectRatio}

IMPORTANT: Return ONLY the raw prompt text without any labels, quotes, or formatting.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      temperature: 0.4,
      max_tokens: 2000,
    });

    let prompt = response?.choices?.[0]?.message?.content;
    
    // CRITICAL CLEANUP: Remove any remaining labels, quotes, or markdown
    prompt = prompt
      ?.replace(/^["']|["']$/g, '')
      ?.replace(/^MidJourney Prompt:\s*/i, '')
      ?.replace(/^Prompt:\s*/i, '')
      ?.replace(/\*\*/g, '')
      ?.replace(/\*/g, '')
      ?.trim();
    
    return prompt;
  } catch (error) {
    handleOpenAIError(error, 'Generate MidJourney prompt');
  }
}

/**
 * Generate titles for all articles based on focus keywords
 */
export async function generateArticleTitles(focusKeywords) {
  try {
    validateAPIKey();
    
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'Generate compelling, SEO-friendly article titles for recipe content.',
        },
        {
          role: 'user',
          content: `Generate engaging article titles for these recipe keywords: ${focusKeywords?.join(', ')}`,
        },
      ],
      temperature: 0.4,
      max_tokens: 2000,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'article_titles',
          schema: {
            type: 'object',
            properties: {
              titles: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    keyword: { type: 'string' },
                    title: { type: 'string' },
                  },
                  required: ['keyword', 'title'],
                },
              },
            },
            required: ['titles'],
            additionalProperties: false,
          },
        },
      },
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    handleOpenAIError(error, 'Generate article titles');
  }
}

/**
 * Generate secondary keywords for articles
 */
export async function generateSecondaryKeywords(focusKeywords) {
  try {
    validateAPIKey();
    
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'Generate relevant secondary keywords for SEO optimization.',
        },
        {
          role: 'user',
          content: `Generate 5-7 secondary keywords for each of these focus keywords: ${focusKeywords?.join(', ')}`,
        },
      ],
      temperature: 0.4,
      max_tokens: 2000,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'secondary_keywords',
          schema: {
            type: 'object',
            properties: {
              keywords: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    focus_keyword: { type: 'string' },
                    secondary_keywords: { type: 'array', items: { type: 'string' } },
                  },
                  required: ['focus_keyword', 'secondary_keywords'],
                },
              },
            },
            required: ['keywords'],
            additionalProperties: false,
          },
        },
      },
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    handleOpenAIError(error, 'Generate secondary keywords');
  }
}

/**
 * Generate 3-5 SEO-optimized title suggestions for a single article
 */
export async function generateSingleArticleTitleSuggestions(focusKeyword) {
  try {
    validateAPIKey();
    
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an SEO expert specializing in creating compelling, click-worthy article titles that rank well in search engines.',
        },
        {
          role: 'user',
          content: `Generate 3-5 SEO-optimized article title suggestions for the focus keyword: "${focusKeyword}"
          
Requirements:
- Titles should be engaging and click-worthy
- Include the focus keyword naturally
- Keep titles between 50-60 characters
- Use power words and emotional triggers
- Vary the title formats (how-to, listicle, question, etc.)
- Make them actionable and valuable
          
Return as a simple JSON array of title strings.`,
        },
      ],
      temperature: 0.7,
      max_tokens: 500,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'title_suggestions',
          schema: {
            type: 'object',
            properties: {
              suggestions: {
                type: 'array',
                items: { type: 'string' }
              }
            },
            required: ['suggestions'],
            additionalProperties: false
          }
        }
      }
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    return result?.suggestions || [];
  } catch (error) {
    handleOpenAIError(error, 'Generate title suggestions');
  }
}

/**
 * Generate keyword cluster based on focus keyword
 */
export async function generateKeywordCluster(focusKeyword) {
  try {
    validateAPIKey();
    
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an SEO expert specializing in keyword research and semantic SEO. Generate comprehensive keyword clusters that improve search visibility.',
        },
        {
          role: 'user',
          content: `Generate a keyword cluster for the focus keyword: "${focusKeyword}"
          
Requirements:
- Include the main focus keyword
- Add 5-10 related long-tail keywords
- Include semantic variations and synonyms
- Add question-based keywords (what, how, why, etc.)
- Format as comma-separated list
- Keep keywords relevant and search-friendly
- Focus on user intent and search behavior
          
Return as a single comma-separated string of keywords.`,
        },
      ],
      temperature: 0.6,
      max_tokens: 300,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'keyword_cluster',
          schema: {
            type: 'object',
            properties: {
              cluster: { type: 'string' }
            },
            required: ['cluster'],
            additionalProperties: false
          }
        }
      }
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    return result?.cluster || focusKeyword;
  } catch (error) {
    handleOpenAIError(error, 'Generate keyword cluster');
  }
}

/**
 * Generate a single primary focus keyword from title/context
 */
export async function generatePrimaryFocusKeyword(articleTitle, articleContext = '') {
  try {
    validateAPIKey();

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an SEO expert. Generate one primary focus keyword that matches the recipe topic exactly.',
        },
        {
          role: 'user',
          content: `Generate ONE primary focus keyword phrase.\n\nTitle: "${articleTitle}"\nContext: "${articleContext}"\n\nRequirements:\n- Must match the title topic exactly\n- 2-6 words\n- Use natural wording (no quotes)\n- Return ONLY the keyword phrase`,
        },
      ],
      temperature: 0.4,
      max_tokens: 30,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'focus_keyword',
          schema: {
            type: 'object',
            properties: {
              keyword: { type: 'string' },
            },
            required: ['keyword'],
            additionalProperties: false,
          },
        },
      },
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    return result?.keyword?.trim();
  } catch (error) {
    handleOpenAIError(error, 'Generate focus keyword');
    return null;
  }
}

/**
 * Generate compelling article description/excerpt for a blog post
 */
export async function generateArticleDescription(focusKeyword, title) {
  try {
    validateAPIKey();
    
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an expert content writer specializing in creating engaging, SEO-friendly article descriptions that capture reader attention and improve search rankings.',
        },
        {
          role: 'user',
          content: `Generate a compelling article description/excerpt for:
Focus Keyword: "${focusKeyword}"
${title ? `Title: "${title}"` : ''}

Requirements:
- Length: 140-160 characters (meta description length)
- Include the focus keyword naturally
- Make it engaging and click-worthy
- Use action words and emotional triggers
- Clearly communicate the article's value
- Optimize for both SEO and user engagement
- Keep it concise but descriptive

Return as a single string.`,
        },
      ],
      temperature: 0.7,
      max_tokens: 200,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'article_description',
          schema: {
            type: 'object',
            properties: {
              description: { type: 'string' }
            },
            required: ['description'],
            additionalProperties: false
          }
        }
      }
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    return result?.description || '';
  } catch (error) {
    console.error('Error in Generate article description:', error);
    // Return empty string instead of throwing - allows article generation to continue
    return '';
  }
}

/**
 * NEW: Generate realistic camera-style EXIF metadata for humanizing AI-generated images
 */
export async function generateRealisticExifMetadata(focusKeyword) {
  try {
    const cameras = [
      'iPhone 15 Pro',
      'Canon EOS R6 Mark II',
      'Sony Alpha 7R IV',
      'Fujifilm X-T5',
      'Nikon Z6 II'
    ];
    
    const photographerNames = [
      'Sarah Mitchell',
      'David Chen',
      'Emma Rodriguez',
      'Michael Thompson',
      'Olivia Anderson',
      'James Patterson',
      'Sophia Williams',
      'Daniel Martinez'
    ];
    
    const cities = [
      { name: 'New York', lat: 40.7128, lon: -74.0060 },
      { name: 'Los Angeles', lat: 34.0522, lon: -118.2437 },
      { name: 'Chicago', lat: 41.8781, lon: -87.6298 },
      { name: 'San Francisco', lat: 37.7749, lon: -122.4194 },
      { name: 'Seattle', lat: 47.6062, lon: -122.3321 },
      { name: 'Austin', lat: 30.2672, lon: -97.7431 },
      { name: 'Miami', lat: 25.7617, lon: -80.1918 },
      { name: 'Boston', lat: 42.3601, lon: -71.0589 }
    ];
    
    // Random selections
    const camera = cameras?.[Math.floor(Math.random() * cameras?.length)];
    const photographer = photographerNames?.[Math.floor(Math.random() * photographerNames?.length)];
    const city = cities?.[Math.floor(Math.random() * cities?.length)];
    const software = Math.random() > 0.5 ? `Adobe Lightroom ${14 + Math.floor(Math.random() * 3)}.${Math.floor(Math.random() * 5)}` : `Adobe Photoshop ${25 + Math.floor(Math.random() * 3)}.${Math.floor(Math.random() * 10)}`;
    
    // Random camera settings
    const exposureTime = `1/${40 + Math.floor(Math.random() * 210)}`;
    const fNumber = (1.8 + Math.random() * 2.2)?.toFixed(1);
    const iso = 80 + Math.floor(Math.random() * 720);
    const focalLength = `${35 + Math.floor(Math.random() * 50)}mm`;
    
    // Random timestamps (within last 30 days)
    const now = new Date();
    const randomDays = Math.floor(Math.random() * 30);
    const randomHours = Math.floor(Math.random() * 24);
    const randomMinutes = Math.floor(Math.random() * 60);
    const captureDate = new Date(now.getTime() - (randomDays * 24 * 60 * 60 * 1000) - (randomHours * 60 * 60 * 1000) - (randomMinutes * 60 * 1000));
    
    return {
      Make: camera?.split(' ')?.[0],
      Model: camera,
      Software: software,
      ExposureTime: exposureTime,
      FNumber: `f/${fNumber}`,
      ISO: iso,
      FocalLength: focalLength,
      WhiteBalance: 'Auto',
      ExposureProgram: 'Aperture Priority',
      MeteringMode: 'Multi-segment',
      Flash: 'Off, Did not fire',
      ColorSpace: 'sRGB',
      ExposureBiasValue: (Math.random() * 2 - 1)?.toFixed(1),
      DateTimeOriginal: captureDate?.toISOString(),
      CreateDate: captureDate?.toISOString(),
      ModifyDate: new Date(captureDate?.getTime() + (Math.random() * 3600000))?.toISOString(),
      GPSLatitude: city?.lat + (Math.random() * 0.1 - 0.05),
      GPSLongitude: city?.lon + (Math.random() * 0.1 - 0.05),
      GPSLatitudeRef: city?.lat >= 0 ? 'N' : 'S',
      GPSLongitudeRef: city?.lon >= 0 ? 'E' : 'W',
      Artist: photographer,
      Copyright: `© ${captureDate?.getFullYear()} ${photographer}`,
      ImageDescription: `${focusKeyword} photographed by ${photographer}`,
      UserComment: `Professional food photography - ${focusKeyword}`
    };
  } catch (error) {
    console.error('Error generating EXIF metadata:', error);
    return null;
  }
}

/**
 * NEW: Auto-detect and select the best matching category for an article
 */
export async function generateCategorySelection(focusKeyword, availableCategories) {
  try {
    if (!availableCategories || availableCategories?.length === 0) {
      return null;
    }

    validateAPIKey();

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an expert content categorization assistant. Analyze the focus keyword and select the SINGLE most appropriate category from the available options.',
        },
        {
          role: 'user',
          content: `Select the BEST SINGLE category for this article:
Focus Keyword: "${focusKeyword}"

Available Categories:
${availableCategories?.map((cat, idx) => `${idx + 1}. ${cat?.name} (ID: ${cat?.id})`)?.join('\n')}

Requirements:
- Select EXACTLY ONE category
- Choose the most relevant match based on topic and context
- Return the category ID as a number
- If no perfect match exists, choose the closest related category`,
        },
      ],
      temperature: 0.3,
      max_tokens: 100,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'category_selection',
          schema: {
            type: 'object',
            properties: {
              categoryId: { type: 'number' }
            },
            required: ['categoryId'],
            additionalProperties: false
          }
        }
      }
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    return result?.categoryId || null;
  } catch (error) {
    console.error('Error generating category selection:', error);
    return null;
  }
}

/**
 * Generate recipe JSON-LD schema
 */
export async function generateRecipeSchema({
  title,
  description,
  imageUrl,
  ingredients,
  instructions,
  prepTime,
  cookTime,
  servings,
  calories,
  focusKeyword,
}) {
  try {
    validateAPIKey();
    
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'Generate valid Recipe schema.org JSON-LD structured data.',
        },
        {
          role: 'user',
          content: `Create Recipe JSON-LD schema for: ${title}
Description: ${description}
Ingredients: ${ingredients}
Instructions: ${instructions}
${prepTime ? `Prep Time: ${prepTime}` : ''}
${cookTime ? `Cook Time: ${cookTime}` : ''}
${servings ? `Servings: ${servings}` : ''}
${calories ? `Calories: ${calories}` : ''}`,
        },
      ],
      temperature: 0.4,
      max_tokens: 2000,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'recipe_schema',
          schema: {
            type: 'object',
            properties: {
              context: { type: 'string' },
              type: { type: 'string' },
              name: { type: 'string' },
              description: { type: 'string' },
              image: { type: 'string' },
              recipeIngredient: { type: 'array', items: { type: 'string' } },
              recipeInstructions: { type: 'array', items: { type: 'object' } },
              prepTime: { type: 'string' },
              cookTime: { type: 'string' },
              totalTime: { type: 'string' },
              recipeYield: { type: 'string' },
              nutrition: { type: 'object' },
            },
            required: ['context', 'type', 'name', 'recipeIngredient', 'recipeInstructions'],
            additionalProperties: false,
          },
        },
      },
    });

    const schema = JSON.parse(response?.choices?.[0]?.message?.content);
    return `<script type="application/ld+json">\n${JSON.stringify(schema, null, 2)}\n</script>`;
  } catch (error) {
    console.error('Error generating recipe schema:', error);
    return '';
  }
}

/**
 * 3-STEP ARTICLE FETCHING METHOD (Browser-Compatible Version)
 */
export async function scrapeWebsiteArticles(websiteUrl, maxArticles = 15) {
  try {
    const baseUrl = websiteUrl?.endsWith('/') ? websiteUrl?.slice(0, -1) : websiteUrl;
    
    console.log('Starting AI-powered article scraping for:', baseUrl);
    
    return await scrapeHomepageArticles(baseUrl, maxArticles);
    
  } catch (error) {
    console.error('Article fetching failed:', error);
    throw error; // Re-throw with enhanced error message from scrapeHomepageArticles
  }
}

/**
 * Scrape homepage article loop using AI (Enhanced Error Handling)
 */
async function scrapeHomepageArticles(baseUrl, maxArticles = 15) {
  try {
    validateAPIKey();

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: `You are an advanced web scraping AI specializing in blog article detection.
          
          CRITICAL RULES FOR HOMEPAGE SCRAPING:
          1. ONLY extract articles from the main blog container/loop
          2. IGNORE: categories, tags, sidebar, footer, old archives
          3. IGNORE: non-article content like "About", "Contact", navigation
          4. Focus on RECENT articles only (newest 10-15 posts)
          5. Sort by publish date (newest first)
          6. Generate realistic recent dates (last 1-30 days)
          7. Return ONLY real blog posts with complete metadata
          
          Article detection patterns:
          - WordPress: main blog loop, post grid, article list
          - Standard blogs: recent posts section, blog feed
          - Avoid: category listings, tag pages, archive pages`
        },
        {
          role: 'user',
          content: `Scrape ONLY the main article loop from homepage: ${baseUrl}
          
          Requirements:
          - Return ${maxArticles} LATEST articles from main blog container
          - Sort by publish date (newest first)
          - Include complete metadata for each article
          - Only return actual blog posts (no categories, tags, sidebars)
          - Use realistic recent dates (within last 1-30 days)
          - Generate proper image URLs from the same domain
          
          Return as JSON array of article objects with:
          - title: Actual article title
          - articleUrl: Full article URL
          - excerpt: 2-3 sentence summary
          - featuredImageUrl: Image URL from same domain
          - focusKeyword: Main topic/keyword
          - publishDate: ISO date string (recent, sorted newest first)`
        }
      ],
      temperature: 0.3,
      max_tokens: 3000,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'scraped_articles',
          schema: {
            type: 'object',
            properties: {
              articles: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    title: { type: 'string' },
                    articleUrl: { type: 'string' },
                    excerpt: { type: 'string' },
                    featuredImageUrl: { type: 'string' },
                    focusKeyword: { type: 'string' },
                    publishDate: { type: 'string' }
                  },
                  required: ['title', 'articleUrl', 'excerpt', 'featuredImageUrl', 'focusKeyword', 'publishDate']
                }
              }
            },
            required: ['articles'],
            additionalProperties: false
          }
        }
      }
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    console.log('✅ AI-powered scraping successful');
    return result?.articles || [];
  } catch (error) {
    handleOpenAIError(error, 'Article scraping');
  }
}

/**
 * DEPRECATED: Use scrapeWebsiteArticles which now returns complete metadata
 */
export async function scrapeArticleMetadata(articleUrl) {
  try {
    validateAPIKey();
    
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: `Generate realistic article metadata based on URL analysis.`
        },
        {
          role: 'user',
          content: `Generate metadata for: ${articleUrl}`
        }
      ],
      temperature: 0.3,
      max_tokens: 500,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'article_metadata',
          schema: {
            type: 'object',
            properties: {
              title: { type: 'string' },
              excerpt: { type: 'string' },
              featured_image_url: { type: 'string' },
              focus_keyword: { type: 'string' },
              publish_date: { type: 'string' }
            },
            required: ['title', 'excerpt', 'featured_image_url', 'focus_keyword', 'publish_date'],
            additionalProperties: false
          }
        }
      }
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    handleOpenAIError(error, 'Scrape article metadata');
  }
}

/**
 * Generate SEO title with Rank Math requirements (max 60 chars, must include number)
 */
export async function generateSEOTitle(focusKeyword, articleTitle) {
  try {
    validateAPIKey();
    
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an SEO expert specializing in creating Rank Math-compliant SEO titles for food blogs.',
        },
        {
          role: 'user',
          content: `Generate an SEO title for this article:
Focus Keyword: "${focusKeyword}"
Article Title: "${articleTitle}"

REQUIREMENTS (CRITICAL):
- Maximum 60 characters (strict limit)
- MUST include at least ONE number (Rank Math requirement)
- Food blog style (engaging, appetizing)
- Include focus keyword naturally
- Use power words when possible

Return ONLY the SEO title as a plain string, no quotes or formatting.`,
        },
      ],
      temperature: 0.7,
      max_tokens: 100,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'seo_title',
          schema: {
            type: 'object',
            properties: {
              title: { type: 'string' }
            },
            required: ['title'],
            additionalProperties: false
          }
        }
      }
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    return result?.title || '';
  } catch (error) {
    console.error('SEO title generation error:', error);
    return '';
  }
}

/**
 * Generate 10 focus keywords (1 primary + 2-3 secondary, comma-separated, no hashtags)
 */
export async function generate10FocusKeywords(focusKeyword, articleTitle) {
  try {
    validateAPIKey();
    
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an SEO expert specializing in keyword research for food blogs and WordPress Rank Math optimization.',
        },
        {
          role: 'user',
          content: `Generate focus keywords for this recipe article:
Focus Keyword: "${focusKeyword}"
Article Title: "${articleTitle}"

REQUIREMENTS:
- First keyword = primary focus keyword (most important)
- Add 2-3 secondary keywords (related, high-value)
- Format: comma-separated list
- NO hashtags (#)
- NO special characters
- Keep keywords natural and search-friendly
- Total: 3-4 keywords maximum

Example format: "chocolate chip cookies, easy cookie recipe, homemade cookies"

Return ONLY the comma-separated keyword string.`,
        },
      ],
      temperature: 0.6,
      max_tokens: 150,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'focus_keywords',
          schema: {
            type: 'object',
            properties: {
              keywords: { type: 'string' }
            },
            required: ['keywords'],
            additionalProperties: false
          }
        }
      }
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    return result?.keywords || '';
  } catch (error) {
    console.error('Focus keywords generation error:', error);
    return '';
  }
}

/**
 * Smart category selection based on recipe title and keywords
 */
export async function selectRecipeCategory(articleTitle, focusKeyword, availableCategories) {
  try {
    validateAPIKey();
    
    const categoryList = availableCategories?.map(c => c?.name)?.join(', ');
    
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an expert at categorizing food blog recipes based on title and keywords. Choose the SINGLE most appropriate category.',
        },
        {
          role: 'user',
          content: `Select the BEST category for this recipe:
Title: "${articleTitle}"
Keywords: "${focusKeyword}"

Available Categories:
${categoryList}

FALLBACK RULES:
- cake/dessert/sweet → Desserts
- chicken/beef/fish/meat → Main Dishes
- salad/greens → Salads
- soup/stew/broth → Soups
- breakfast/pancake/waffle → Breakfast

Return ONLY the category name (exact match from the list above).`,
        },
      ],
      temperature: 0.3,
      max_tokens: 50,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'category_selection',
          schema: {
            type: 'object',
            properties: {
              category: { type: 'string' }
            },
            required: ['category'],
            additionalProperties: false
          }
        }
      }
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    return result?.category || '';
  } catch (error) {
    handleOpenAIError(error, 'Category selection');
    throw error;
  }
}

/**
 * Extract ONE main SEO keyword (3-6 words) from article content
 */
export async function extractMainSEOKeyword(title, excerpt, metaDescription) {
  try {
    validateAPIKey();
    
    let content = `Title: ${title || ''}
Excerpt: ${excerpt || ''}
Meta: ${metaDescription || ''}`;
    
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an SEO expert. Extract ONE main SEO keyword phrase (3-6 words) that best represents the article topic. Return ONLY the keyword phrase, nothing else.'
        },
        {
          role: 'user',
          content: `Extract the main SEO keyword from this article:\n\n${content}`
        }
      ],
      temperature: 0.3,
      max_tokens: 20
    });
    
    const keyword = response?.choices?.[0]?.message?.content?.trim();
    return keyword || null;
  } catch (error) {
    handleOpenAIError(error, 'AI keyword extraction');
    return null;
  }
}

/**
 * Generate Rank Math SEO Title (50-60 chars with at least one number)
 */
export async function generateRankMathSEOTitle(focusKeyword, articleTitle) {
  try {
    validateAPIKey();
    
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an SEO expert specializing in Rank Math optimization. Generate SEO titles that are 50-60 characters, include the focus keyword naturally, and contain at least one number for better CTR. Return ONLY the title, nothing else.'
        },
        {
          role: 'user',
          content: `Generate an SEO title for:\nFocus Keyword: ${focusKeyword}\nArticle Title: ${articleTitle}\n\nRequirements:\n- Length: 50-60 characters\n- Must include focus keyword\n- Must include at least one number\n- Engaging and click-worthy`
        }
      ],
      temperature: 0.7,
      max_tokens: 30
    });
    
    let seoTitle = response?.choices?.[0]?.message?.content?.trim();
    
    // Ensure it has a number
    if (!/\d/?.test(seoTitle)) {
      // Add a number if missing
      const currentYear = new Date()?.getFullYear();
      seoTitle = `${seoTitle} ${currentYear}`;
    }
    
    // Trim to 60 chars if too long
    if (seoTitle?.length > 60) {
      seoTitle = seoTitle?.substring(0, 57) + '...';
    }
    
    return seoTitle;
  } catch (error) {
    handleOpenAIError(error, 'SEO title generation');
    return null;
  }
}

/**
 * Generate Rank Math Meta Description (140-160 chars with focus keyword)
 */
export async function generateRankMathMetaDescription(focusKeyword, articleTitle, articleExcerpt) {
  try {
    validateAPIKey();
    
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: 'You are an SEO expert specializing in Rank Math optimization. Generate meta descriptions that are 140-160 characters, include the focus keyword naturally, and are compelling for search results. Return ONLY the description, nothing else.'
        },
        {
          role: 'user',
          content: `Generate a meta description for:\nFocus Keyword: ${focusKeyword}\nTitle: ${articleTitle}\nExcerpt: ${articleExcerpt || ''}\n\nRequirements:\n- Length: 140-160 characters\n- Must include focus keyword naturally\n- Compelling and actionable\n- Encourage clicks`
        }
      ],
      temperature: 0.7,
      max_tokens: 60
    });
    
    let metaDesc = response?.choices?.[0]?.message?.content?.trim();
    
    // Trim to 160 chars if too long
    if (metaDesc?.length > 160) {
      metaDesc = metaDesc?.substring(0, 157) + '...';
    }
    
    // Ensure minimum length
    if (metaDesc?.length < 140) {
      metaDesc = metaDesc + ' Learn more about ' + focusKeyword + '.';
      if (metaDesc?.length > 160) {
        metaDesc = metaDesc?.substring(0, 160);
      }
    }
    
    return metaDesc;
  } catch (error) {
    handleOpenAIError(error, 'Meta description generation');
    return null;
  }
}

/**
 * Generate SEO-friendly permalink slug with auto-incrementing number
 */
export function generateRankMathPermalink(focusKeyword, existingSlugs = []) {
  // Convert focus keyword to slug format
  let baseSlug = focusKeyword
    ?.toLowerCase()
    ?.replace(/[^a-z0-9\s-]/g, '') // Remove special chars
    ?.replace(/\s+/g, '-') // Replace spaces with hyphens
    ?.replace(/-+/g, '-') // Remove duplicate hyphens
    ?.trim();
  
  // Find the next available number
  let counter = 1;
  let slug = `${baseSlug}-${counter}`;
  
  while (existingSlugs?.includes(slug)) {
    counter++;
    slug = `${baseSlug}-${counter}`;
  }
  
  return slug;
}

/**
 * Ensure focus keyword appears at least 15 times naturally in content
 */
export async function ensureKeywordDensity(articleContent, focusKeyword, targetCount = 15) {
  try {
    // Count current occurrences (case-insensitive)
    const regex = new RegExp(focusKeyword?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    const matches = articleContent?.match(regex);
    const currentCount = matches?.length || 0;
    
    if (currentCount >= targetCount) {
      return {
        content: articleContent,
        keywordCount: currentCount,
        modified: false
      };
    }
    
    // Need to add more keywords naturally
    validateAPIKey();
    
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: `You are an SEO content optimizer. Your task is to naturally integrate the focus keyword "${focusKeyword}" into the article content at least ${targetCount - currentCount} more times. The keyword should be added naturally without disrupting readability. Return the COMPLETE modified article content in HTML format.`
        },
        {
          role: 'user',
          content: `Current article content (has ${currentCount} occurrences of "${focusKeyword}"):\n\n${articleContent}\n\nAdd "${focusKeyword}" ${targetCount - currentCount} more times naturally throughout the content. Maintain HTML formatting and readability.`
        }
      ],
      temperature: 0.5,
      max_tokens: 4000
    });
    
    const modifiedContent = response?.choices?.[0]?.message?.content?.trim();
    const newMatches = modifiedContent?.match(regex);
    const newCount = newMatches?.length || 0;
    
    return {
      content: modifiedContent,
      keywordCount: newCount,
      modified: true
    };
  } catch (error) {
    handleOpenAIError(error, 'Keyword density optimization');
    return {
      content: articleContent,
      keywordCount: currentCount,
      modified: false,
      error: error?.message
    };
  }
}

function openaiService(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: openaiService is not implemented yet.', args);
  return null;
}

export default openaiService;
